import type { MasterDataVehicleCreatePageProps } from "@/pages/master-data-vehicle-create/model";
import type { CreateMasterDataVehicleFormSchemaType } from "@/pages/master-data-vehicle-create/model/validation";
import { VEHICLE_WEIGHT_CLASSES } from "@/shared/constants/offer-cargo";
import {
  VEHICLE_EQIUIPMENTS,
  VEHICLE_SHAPES_LIST,
} from "@/shared/constants/vehicle";
import type { LoadableData } from "@/shared/lib/loadable";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseCheckboxGroup } from "@/shared/ui/base-checkbox-group";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import {
  AccordionDetails,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
} from "@mui/material";
import { type Control, Controller } from "react-hook-form";

export const MasterDataVehicleForm = ({
  offices,
  control,
}: {
  offices: LoadableData<MasterDataVehicleCreatePageProps["offices"]>;
  control: Control<CreateMasterDataVehicleFormSchemaType>;
}) => {
  return (
    <BaseAccordion>
      <BaseAccordionSummary>車両</BaseAccordionSummary>
      <AccordionDetails>
        <Stack spacing={3} pb={4}>
          <BaseFormLabel
            label="車両ナンバー"
            isRequired
            sx={{ maxWidth: "376px" }}
          >
            <Controller
              name="number"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  placeholder="品川 500 あ 1234"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel label="事業所" sx={{ maxWidth: "376px" }}>
            <Controller
              name="officeId"
              control={control}
              render={({ field, fieldState }) => (
                <BaseSelect
                  {...field}
                  size="small"
                  type="number"
                  fullWidth
                  noSelectedOptionText="(選択しない)"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                >
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id}>
                      {office.name}
                    </MenuItem>
                  ))}
                </BaseSelect>
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel label="車名" sx={{ maxWidth: "376px" }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
            <BaseFormLabel label="トン数(車格)" sx={{ maxWidth: "376px" }}>
              <Controller
                name="weightClass"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl size="small" sx={{ maxWidth: "240px" }}>
                      <BaseSelect
                        {...field}
                        type="number"
                        placeholder="未選択"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        noSelectedOptionText="(選択しない)"
                        sx={{ minWidth: "240px" }}
                      >
                        {VEHICLE_WEIGHT_CLASSES.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </BaseSelect>
                    </FormControl>
                  );
                }}
              />
            </BaseFormLabel>
            <BaseFormLabel
              label="ボディタイプ(車体の形状)"
              sx={{ maxWidth: "240px" }}
            >
              <Controller
                name="shape"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl size="small" fullWidth>
                    <BaseSelect
                      {...field}
                      type="string"
                      placeholder="未選択"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      noSelectedOptionText="(選択しない)"
                      sx={{ minWidth: "240px" }}
                    >
                      {VEHICLE_SHAPES_LIST.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </BaseSelect>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </BaseFormLabel>
          </Stack>
          <BaseFormLabel label="装備品">
            <Controller
              name="vehicleEquipments"
              control={control}
              render={({ field, fieldState }) => (
                <BaseCheckboxGroup
                  {...field}
                  options={VEHICLE_EQIUIPMENTS.map((item) => ({
                    key: item.value,
                    label: item.label,
                  }))}
                  selectedValues={field.value ?? []}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message || "複数選択可能です。"}
                />
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel label="その他・特記事項">
            <Controller
              name="memo"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  sx={{
                    maxWidth: "376px",
                  }}
                />
              )}
            />
          </BaseFormLabel>
        </Stack>
      </AccordionDetails>
    </BaseAccordion>
  );
};
